import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Check, Users, Calendar, FileText, BarChart3, Shield, Zap } from "lucide-react";
import { PageHeader } from "@/components/layout/Navigation";

const iconMap = { Users, Calendar, FileText, BarChart3, Shield, Zap };

const solutions = {
  "client-management": {
    icon: "Users",
    title: "Client Management",
    tagline: "Centralized Patient Data",
    description: "Keep all your patient information organized and accessible in one secure place. Our client management system provides a comprehensive 360-degree view of every patient, from contact details and demographics to complete visit history and communication logs. Easily track patient preferences, notes, and custom fields tailored to your practice needs.",
    benefits: ["Unified patient profiles with complete history", "Contact & communication tracking", "Custom fields & tags for organization", "Secure, HIPAA-compliant data storage", "Quick search & advanced filters", "Import/export patient data easily"],
    image: "https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=800",
    features: [
      { name: "Patient Profiles", description: "Comprehensive profiles with demographics, insurance, and medical history" },
      { name: "Communication Log", description: "Track all calls, emails, and messages in one place" },
      { name: "Custom Fields", description: "Add fields specific to your practice specialty" },
      { name: "Smart Search", description: "Find any patient instantly with powerful search" }
    ]
  },
  "scheduling": {
    icon: "Calendar",
    title: "Smart Scheduling",
    tagline: "Automated Appointments",
    description: "Reduce no-shows and administrative burden with intelligent scheduling that works around the clock. Enable patients to book appointments online, send automated reminders via text and email, and sync seamlessly with your existing calendar. Our waitlist management ensures you fill last-minute cancellations automatically.",
    benefits: ["Online self-booking portal", "Automated SMS & email reminders", "Calendar sync (Google, Outlook, Apple)", "Waitlist management", "Recurring appointment scheduling", "Multi-provider calendar views"],
    image: "https://images.unsplash.com/photo-1506784983877-45594efa4cbe?w=800",
    features: [
      { name: "Online Booking", description: "Let patients book appointments 24/7 from your website" },
      { name: "Smart Reminders", description: "Reduce no-shows by up to 50% with automated reminders" },
      { name: "Calendar Sync", description: "Two-way sync with Google, Outlook, and Apple calendars" },
      { name: "Waitlist", description: "Automatically fill cancellations from your waitlist" }
    ]
  },
  "documentation": {
    icon: "FileText",
    title: "Documentation",
    tagline: "Go Paperless",
    description: "Streamline your documentation workflow with digital forms, customizable templates, and secure cloud storage. Reduce errors and save countless hours with smart form automation, e-signatures, and automatic organization. Access any document from anywhere, anytime.",
    benefits: ["Custom form builder", "E-signatures for consent forms", "Template library for common documents", "Automatic filing and organization", "Version history and audit trails", "Secure cloud storage"],
    image: "https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=800",
    features: [
      { name: "Form Builder", description: "Create custom intake forms, assessments, and questionnaires" },
      { name: "E-Signatures", description: "Collect legally-binding signatures electronically" },
      { name: "Templates", description: "Start with pre-built templates or create your own" },
      { name: "Auto-Organization", description: "Documents are automatically filed to patient records" }
    ]
  },
  "analytics": {
    icon: "BarChart3",
    title: "Analytics & Reports",
    tagline: "Data-Driven Insights",
    description: "Make informed decisions with real-time analytics and customizable reports. Track key performance indicators, identify trends, and uncover opportunities to optimize your practice. From revenue analysis to patient retention metrics, get the insights you need to grow.",
    benefits: ["Revenue and billing tracking", "Appointment analytics and trends", "Patient retention metrics", "Custom dashboards", "Automated scheduled reports", "Export to PDF and Excel"],
    image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800",
    features: [
      { name: "Revenue Dashboard", description: "Track collections, outstanding balances, and trends" },
      { name: "Appointment Insights", description: "Analyze booking patterns, no-shows, and utilization" },
      { name: "Patient Analytics", description: "Understand retention, referral sources, and demographics" },
      { name: "Custom Reports", description: "Build reports specific to your practice needs" }
    ]
  },
  "integrations": {
    icon: "Zap",
    title: "Integrations",
    tagline: "Connected Ecosystem",
    description: "Connect NeoBody with your existing tools and workflows. Our platform integrates seamlessly with leading EHR systems, billing software, labs, telehealth providers, and more. Use our API to build custom integrations specific to your practice.",
    benefits: ["EHR/EMR integration", "Billing & payment processing", "Lab ordering connections", "Telehealth platforms", "API access for developers", "Zapier integration"],
    image: "https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=800",
    features: [
      { name: "EHR Integration", description: "Connect with Epic, Cerner, Athena, and more" },
      { name: "Payment Processing", description: "Accept cards, HSA/FSA, and process insurance" },
      { name: "Labs & Imaging", description: "Order labs and receive results electronically" },
      { name: "Open API", description: "Build custom integrations with our REST API" }
    ]
  },
  "security": {
    icon: "Shield",
    title: "Security & Compliance",
    tagline: "HIPAA Certified",
    description: "Your patients' data security is our top priority. NeoBody is fully HIPAA compliant with enterprise-grade encryption, strict access controls, and comprehensive audit logging. We undergo regular third-party security audits to ensure the highest standards of protection.",
    benefits: ["Full HIPAA compliance", "256-bit AES encryption", "Role-based access controls", "Complete audit logging", "Regular security audits", "SOC 2 Type II certified"],
    image: "https://images.unsplash.com/photo-1563986768609-322da13575f3?w=800",
    features: [
      { name: "HIPAA Compliance", description: "Fully compliant with all HIPAA requirements" },
      { name: "Encryption", description: "Data encrypted at rest and in transit" },
      { name: "Access Controls", description: "Define who can see and do what" },
      { name: "Audit Logs", description: "Complete record of all system activity" }
    ]
  }
};

const ServiceDetailPage = () => {
  const { slug } = useParams();
  const solution = solutions[slug];

  if (!solution) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center">
        <h1 className="font-display text-4xl text-[#1A1A1A] mb-4">Solution Not Found</h1>
        <Link to="/services" className="btn-primary">View All Solutions</Link>
      </div>
    );
  }

  const IconComponent = iconMap[solution.icon] || Users;

  return (
    <div data-testid="service-detail-page">
      <PageHeader 
        title={solution.title}
        subtitle={solution.tagline}
        breadcrumbs={[
          { name: "Solutions", href: "/services" },
          { name: solution.title }
        ]}
      />

      {/* Hero Image */}
      <section className="relative h-[50vh] min-h-[400px]">
        <img 
          src={solution.image} 
          alt={solution.title}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#F9F9F7] to-transparent" />
      </section>

      {/* Content */}
      <section className="py-20 md:py-32 bg-[#F9F9F7] -mt-32 relative z-10">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-16">
            {/* Main Content */}
            <div className="lg:col-span-2">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                <div className="flex items-center gap-4 mb-8">
                  <div className="w-16 h-16 rounded-2xl bg-[#1B4D3E] flex items-center justify-center">
                    <IconComponent className="w-8 h-8 text-white" strokeWidth={1.5} />
                  </div>
                  <div>
                    <span className="text-sm tracking-[0.15em] uppercase text-[#D4AF37] font-medium">
                      {solution.tagline}
                    </span>
                    <h2 className="font-display text-3xl text-[#1A1A1A]">{solution.title}</h2>
                  </div>
                </div>

                <div className="prose prose-lg max-w-none mb-12">
                  <p className="text-[#4A4A4A] font-light leading-relaxed text-lg">
                    {solution.description}
                  </p>
                </div>

                {/* Benefits */}
                <div className="mb-12">
                  <h3 className="font-display text-2xl text-[#1A1A1A] mb-6">Key Benefits</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {solution.benefits.map((benefit, index) => (
                      <div 
                        key={index}
                        className="flex items-center gap-3 p-4 bg-white rounded-xl border border-[#1B4D3E]/10"
                      >
                        <div className="w-8 h-8 rounded-full bg-[#1B4D3E]/10 flex items-center justify-center shrink-0">
                          <Check className="w-4 h-4 text-[#1B4D3E]" />
                        </div>
                        <span className="text-[#1A1A1A]">{benefit}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Features */}
                {solution.features && solution.features.length > 0 && (
                  <div>
                    <h3 className="font-display text-2xl text-[#1A1A1A] mb-6">Features</h3>
                    <div className="space-y-4">
                      {solution.features.map((feature, index) => (
                        <div 
                          key={index}
                          className="p-6 bg-white rounded-2xl border border-[#1B4D3E]/10 hover:border-[#D4AF37]/50 transition-colors"
                        >
                          <h4 className="font-display text-xl text-[#1A1A1A] mb-2">{feature.name}</h4>
                          <p className="text-[#4A4A4A] font-light">{feature.description}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </motion.div>
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="sticky top-28"
              >
                <div className="bg-white rounded-3xl p-8 border border-[#1B4D3E]/10 shadow-lg">
                  <h3 className="font-display text-2xl text-[#1A1A1A] mb-4">
                    See It In Action
                  </h3>
                  <p className="text-[#4A4A4A] font-light mb-6">
                    Schedule a personalized demo to see how {solution.title.toLowerCase()} can transform your practice.
                  </p>
                  <Link to="/book" className="btn-gold w-full justify-center mb-4">
                    Request Demo
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                  <Link to="/contact" className="btn-secondary w-full justify-center">
                    Contact Sales
                  </Link>
                </div>

                <div className="mt-8 p-6 bg-[#1B4D3E] rounded-2xl text-white">
                  <h4 className="font-display text-lg mb-3">Have Questions?</h4>
                  <p className="text-white/70 font-light text-sm mb-4">
                    Our team is ready to help you find the right solution for your practice.
                  </p>
                  <a href="tel:+13473428822" className="text-[#D4AF37] font-medium hover:underline">(347) 342-8822</a>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServiceDetailPage;
